/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateGeneralConfig } from 'state/thunks/commenting';
import { setIsCommentingUnitChanged, setTempCommentingUnitConfig } from 'state/slices/commentingv2'
// material
import ConfigV2Controller from 'material/expandablesection/v2/configv2controller/configv2controller'
import ExpandableSection from 'material/expandablesection/expandablesection'
import { ReactComponent as GridIcon } from 'assets/icons/grid.svg'
// content
import EmoticonCommentReactions from './emoticoncommentreactions/emoticoncommentreactions';
import MediaComments from './mediacomments/mediacomments';
import Sorting from './sorting/sorting';
// styles
import injectSheet from 'react-jss'
import styles from './generalsection_styles'
import CommentLimits from './commentlimits/commentlimits';
import PlaceholderText from './placeholdertext/placeholdertext';
import HeaderTitle from './headertitle/headertitle';

const commentTreeMap = {
  '5_1': 'BASIC',
  '2_0': 'COMPACT',
  '10_3': 'ADVANCED'
}

/* ========== ~~~~~~~~~~ COMMENTING CONFIG V2: GENERAL SECTION ~~~~~~~~~~ ========== */
const GeneralSection = injectSheet(styles)((props) => {
  
  const dispatch = useDispatch()
  const { generalConfig: savedGeneralConfig, commentingUnitId } = useSelector(state => state.commentingV2.commentingUnitConfig)
  const { generalConfig: tempGeneralConfig } = useSelector(state => state.commentingV2.tempCommentingUnitConfig)
  const { isConfigValid } = useSelector(state => state.commentingV2)

  const [generalConfig, setGeneralConfig] = useState(savedGeneralConfig)
  const [isOpened, setIsOpened] = useState(false)

  // useEffect(() => {
  //   if(generalConfig !== savedGeneralConfig && !isOpened) setGeneralConfig(savedGeneralConfig)
  // }, [savedGeneralConfig, isOpened, generalConfig])

  const handleToggle = () => setIsOpened(!isOpened)

  const handleSave = useCallback(async (e) => {
    e.stopPropagation()
    const allowVideos = generalConfig.allowVideos && (generalConfig.allowYoutube || generalConfig.allowVimeo)
    const generalConfigRequest = {...generalConfig, allowVideos}
    const response = await dispatch(updateGeneralConfig({ generalConfig: generalConfigRequest, commentingUnitId }))
    if(response.payload && !response.payload.error) setIsOpened(false)
  }, [commentingUnitId, dispatch, generalConfig])

  const handleCancel = useCallback(async() => {
    setIsOpened(false)
    setGeneralConfig(savedGeneralConfig)
    dispatch(setTempCommentingUnitConfig({
      configName: 'generalConfig',
      data: savedGeneralConfig
    }))
  }, [dispatch, savedGeneralConfig])

  const isChanged = useMemo(() => {
    const isFieldsChanged = !(JSON.stringify(tempGeneralConfig) === JSON.stringify(savedGeneralConfig));
    return isFieldsChanged
  }, [tempGeneralConfig, savedGeneralConfig])


  const handleGeneralConfigChange = useCallback((property, value, additionalProperties) => {
    setGeneralConfig({
      ...generalConfig,
      [property]: value,
      ...additionalProperties
    })
    dispatch(setTempCommentingUnitConfig({
      configName: 'generalConfig',
      data: {
        ...generalConfig,
        [property]: value,
        ...additionalProperties
      }
    }))
}, [dispatch, generalConfig]);

  const controller = (
    <ConfigV2Controller
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={savedGeneralConfig.isUpdating}
      disableSave={!isChanged || isConfigValid.generalConfig === false}
      saveTitle={'Save Changes'}
      icon={<GridIcon />}
      title='General'
    />
  );


  const generalConfigData = useMemo((section) => {
    return {
      emoticonCommentReactions: {
        allowReaction: generalConfig.allowReaction
      },
      mediaComments: { 
        allowLinks: generalConfig.allowLinks, 
        allowRichMedia: generalConfig.allowRichMedia, 
        allowVideos: generalConfig.allowVideos,
        allowYoutube: generalConfig.allowYoutube,
        allowVimeo: generalConfig.allowVimeo,
        allowImages: generalConfig.allowImages,
        allowGifs: generalConfig.allowGifs
      },
      sorting: {
        defaultSort: generalConfig.defaultSort,
        useNewestCommentsSort: generalConfig.useNewestCommentsSort,
        useMostPopularThreadsSort: generalConfig.useMostPopularThreadsSort,
        useMostRepliedThreadsSort: generalConfig.useMostRepliedThreadsSort,
        useOldestCommentsSort: generalConfig.useOldestCommentsSort,
        useMyCommentsSort: generalConfig.useMyCommentsSort
      },
      placeholderText: {
        inputFieldPlaceholderType: generalConfig.inputFieldPlaceholderType,
        noCommentPlaceholder: generalConfig.noCommentPlaceholder,
        beforeFirstCommentPlaceholder: generalConfig.beforeFirstCommentPlaceholder,
        afterFirstCommentPlaceholder: generalConfig.afterFirstCommentPlaceholder
      },
      commentLimits: {
        treeType: generalConfig.treeType ? generalConfig.treeType : (commentTreeMap[`${generalConfig.commentsInitialLoadLimit}_${generalConfig.repliesInitialLoadLimit}`] || 'CUSTOM'),
        commentsInitialLoadLimit: generalConfig.commentsInitialLoadLimit,
        repliesInitialLoadLimit: generalConfig.repliesInitialLoadLimit,
        commentsLoadMoreLimit: generalConfig.commentsLoadMoreLimit,
        repliesLoadMoreLimit: generalConfig.repliesLoadMoreLimit
      },
      header: {
        useHeader: generalConfig.useHeader,
        headerTitle: generalConfig.headerTitle,
        useHeaderBorder: generalConfig.useHeaderBorder,
        useHeaderCounter: generalConfig.useHeaderCounter,
        headerIcon: generalConfig.headerIcon
      }
    }
  }, [generalConfig])

  return (
    <ExpandableSection
      className={`expandablesectionv2 generalsection ${props.classes.generalsection}`}
      controller={controller}
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={savedGeneralConfig.isUpdating}
      disableSave={!isChanged}
    >
      <HeaderTitle onChange={handleGeneralConfigChange} {...generalConfigData.header} />
      <CommentLimits onChange={handleGeneralConfigChange} {...generalConfigData.commentLimits}  />
      <Sorting onChange={handleGeneralConfigChange} {...generalConfigData.sorting} />
      <PlaceholderText onChange={handleGeneralConfigChange} {...generalConfigData.placeholderText}  />
      <EmoticonCommentReactions onChange={handleGeneralConfigChange} {...generalConfigData.emoticonCommentReactions} />
      <MediaComments onChange={handleGeneralConfigChange}  {...generalConfigData.mediaComments} />
    </ExpandableSection>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default GeneralSection;